import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

export default class WizardTourPopupWizardFirstComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('store') declare storeService: Service.Store;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.FIRST_WIZARD_SHOWN);
	}

	@action
	choose(choice: string): void {
		this.analyticsService.event(TrackingEvent.FIRST_WIZARD_COMPLETED, { choice });

		switch (choice) {
			case 'boards':
				this.navigationService
					.transitionToRoute('boards.public-templates.index')
					.then(() => {
						const allSources = this.mmSourcesService.savedRecords,
							sources = this.mmSourcesService
								.createModelsFromRecordsArray(allSources)
								.filter((item) => item.hasBasicMetrics),
							firstConnectedSourceType = sources[0]?.sourceType?.key || 'all';

						window.location.hash = `all|${firstConnectedSourceType}|all`;
					});
				break;
			case 'sources':
				this.navigationService.transitionToRoute('datamanager.available.index');
				break;
			case 'metrics':
				this.navigationService.transitionToRoute('metric-library');
				break;
		}
	}

	@action
	skip(): void {
		this.analyticsService.event(TrackingEvent.FIRST_WIZARD_SKIPPED);
		this.close();
	}
	<template>
		<div class="content">
			<div class="sides-ct has-wide-left-side">

				<div class="side side-left">

					<CommonUiIconComponent @name="Wizard" @class="wizard-icon" />
					{{! shown only when side-left is small }}

					<div class="ct">
						<img
							src="/images/wizards/first-left.png"
							width="200"
							height="160"
							alt="Wizard"
						/>

						<small>Wizard</small>

						<h2>What would you like to do next?</h2>

						<p>
							Use our wizard to create a dashboard from a template, connect additional
							data sources or find more metrics.
						</p>
					</div>

				</div>

				<div class="side side-right">

					<h2>What would you like to do next?</h2>
					{{! shown only on mobile when side-left hidden }}

					<ul class="items-list items-list--selectable">

						<li role="button" class="item" {{on "click" (fn this.choose "boards")}}>
							<img
								src="/images/wizards/first-right-boards.png"
								width="63"
								height="63"
								alt="Create dashboard"
							/>
							<div class="t">New dashboard from template</div>
							<p>
								Browse our library with 200+ pre-made dashboard templates and start
								tracking performance in minutes.
							</p>
							<CommonUiIconComponent
								@name="ArrowRight"
								@size="md"
								@class="item-arrow"
							/>
						</li>

						<li role="button" class="item" {{on "click" (fn this.choose "sources")}}>
							<img
								class="source"
								src="/images/wizards/first-right-sources.png"
								width="93"
								height="80"
								alt="Connect source"
							/>
							<div class="t">Get all of your data into Databox</div>
							<p>
								Connect via any of our 70+ integrations, connect to data stored in
								Google Sheets or a SQL database, send via API or Zapier.
							</p>
							<CommonUiIconComponent
								@name="ArrowRight"
								@size="md"
								@class="item-arrow"
							/>
						</li>

						<li role="button" class="item" {{on "click" (fn this.choose "metrics")}}>
							<img
								src="/images/wizards/first-right-metrics.png"
								width="63"
								height="67"
								alt="Add metrics"
							/>
							<div class="t">Add more metrics</div>
							<p>
								We've made it easy for you to monitor all of your company's metrics
								from any source on one screen.
							</p>
							<CommonUiIconComponent
								@name="ArrowRight"
								@size="md"
								@class="item-arrow"
							/>
						</li>

					</ul>

					<a class="skip" role="button" {{on "click" this.skip}}>
						I would like to explore on my own
					</a>

				</div>

			</div>

		</div>
	</template>
}
